
import { useState, useRef } from "react";
import { toast } from "sonner";

// Define and export the return type interface
export interface UseAutoFetchStateReturn {
  isAutoFetching: boolean;
  setIsAutoFetching: (value: boolean) => void;
  autoFetchPaused: boolean;
  setAutoFetchPaused: (value: boolean) => void;
  fetchedPagesCount: number;
  setFetchedPagesCount: (value: number | ((prev: number) => number)) => void;
  totalVideosFetched: number;
  setTotalVideosFetched: (value: number | ((prev: number) => number)) => void;
  fetchErrored: boolean;
  setFetchErrored: (value: boolean) => void;
  lastFetchedUrl: string;
  setLastFetchedUrl: (value: string) => void;
  autoFetchingRef: React.MutableRefObject<boolean>;
  shouldContinueRef: React.MutableRefObject<boolean>;
  pauseAutoFetch: () => void;
  resumeAutoFetch: () => void;
  cancelAutoFetch: () => void;
}

export function useAutoFetchState(): UseAutoFetchStateReturn {
  const [isAutoFetching, setIsAutoFetching] = useState(false);
  const [autoFetchPaused, setAutoFetchPaused] = useState(false);
  const [fetchedPagesCount, setFetchedPagesCount] = useState(0);
  const [totalVideosFetched, setTotalVideosFetched] = useState(0);
  const [fetchErrored, setFetchErrored] = useState(false);
  const [lastFetchedUrl, setLastFetchedUrl] = useState("");
  const autoFetchingRef = useRef<boolean>(false);
  const shouldContinueRef = useRef<boolean>(true);

  const pauseAutoFetch = () => {
    setAutoFetchPaused(true);
    toast.info("Auto-fetch paused", {
      description: `${totalVideosFetched} videos fetched so far`
    });
  };

  const resumeAutoFetch = () => {
    setAutoFetchPaused(false);
    toast.info("Auto-fetch resumed", {
      description: "Continuing to fetch videos"
    });
  };

  const cancelAutoFetch = () => {
    shouldContinueRef.current = false;
    setIsAutoFetching(false);
    setAutoFetchPaused(false);
    
    toast.info("Auto-fetch cancelled", {
      description: `Stopped after fetching ${totalVideosFetched} videos`
    });
  };

  return {
    isAutoFetching,
    setIsAutoFetching,
    autoFetchPaused,
    setAutoFetchPaused,
    fetchedPagesCount,
    setFetchedPagesCount,
    totalVideosFetched,
    setTotalVideosFetched,
    fetchErrored,
    setFetchErrored,
    lastFetchedUrl,
    setLastFetchedUrl,
    autoFetchingRef,
    shouldContinueRef,
    pauseAutoFetch,
    resumeAutoFetch,
    cancelAutoFetch
  };
}
